import { useEffect, useState , useCallback} from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  CheckCircle2,
  XCircle,
  ExternalLink,
  RefreshCw,
  Loader2,
  Newspaper,
  Clock,
  Bug,
  Sparkles,
} from "lucide-react";

/**
 * AdminNewsQueueTab
 * Admin surface for the RSS-ingested news queue.
 *
 *   • Tabs: Pending / Published / Rejected
 *   • Approve → article goes live on public /news
 *   • Reject  → article is hidden forever (kept as tombstone, so it won't
 *               re-ingest on the next cron run)
 *   • "Refresh feeds now" button bypasses the 6-hour cron
 *
 * Backend: /api/admin/news/{queue|approve/{id}|reject/{id}|ingest}
 */
export default function AdminNewsQueueTab() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ingesting, setIngesting] = useState(false);
  const [healingImages, setHealingImages] = useState(false);
  const [busyId, setBusyId] = useState(null);

    const fetchQueue = useCallback(async (which = status) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/news/queue`, {
        params: { status: which, limit: 100 },
        withCredentials: true,
      });
      setRows(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchQueue(status);
  }, [fetchQueue, status]);

  const runIngest = async () => {
    setIngesting(true);
    try {
      const r = await axios.post(
        `${API}/admin/news/ingest`,
        {},
        { withCredentials: true }
      );
      toast.success(
        `Fetched ${r.data.fetched}, added ${r.data.inserted}, skipped ${r.data.skipped}.`
      );
      await fetchQueue(status);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ingest failed");
    } finally {
      setIngesting(false);
    }
  };

  const healImages = async (force = false) => {
    setHealingImages(true);
    try {
      const r = await axios.post(
        `${API}/admin/news/backfill-images?limit=200${force ? "&force=true" : ""}`,
        {},
        { withCredentials: true }
      );
      toast.success(
        `Scanned ${r.data.scanned}, healed ${r.data.healed}, skipped ${r.data.skipped}.`
      );
      await fetchQueue(status);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setHealingImages(false);
    }
  };

  const [rotating, setRotating] = useState(false);
  const runAutoRotate = async () => {
    setRotating(true);
    try {
      const r = await axios.post(
        `${API}/admin/news/rotate`,
        {},
        { withCredentials: true }
      );
      toast.success(
        `Rotated: retired ${r.data.retired} old articles, promoted ${r.data.promoted} new ones.`
      );
      await fetchQueue(status);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rotate failed");
    } finally {
      setRotating(false);
    }
  };

  const approve = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/news/approve/${id}`, {}, { withCredentials: true });
      toast.success("Approved — now live on /news");
      setRows((rs) => rs.filter((r) => r.article_id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/news/reject/${id}`, {}, { withCredentials: true });
      toast.success("Rejected");
      setRows((rs) => rs.filter((r) => r.article_id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-tbc-gold" />
            News Queue
          </h2>
          <p className="text-sm text-gray-500 font-body mt-1">
            RSS pulls every 6 hours. Every day at 05:00 UTC we <strong>auto-rotate 10 out and 10 in</strong> — you never have to click approve.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={runAutoRotate}
            disabled={rotating}
            variant="outline"
            className="border-tbc-gold text-tbc-navy hover:bg-tbc-gold/20"
            data-testid="news-rotate-now"
          >
            {rotating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Rotate now (10↔10)
          </Button>
          <Button
            onClick={() => healImages(false)}
            disabled={healingImages}
            variant="outline"
            className="border-tbc-navy text-tbc-navy hover:bg-tbc-navy hover:text-white"
            data-testid="news-heal-images-btn"
          >
            {healingImages ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Heal missing images
          </Button>
          <Button
            onClick={() => {
              if (!window.confirm("Re-download and re-mirror EVERY article's image?\n\nThis rehosts every image to our object storage, replacing any prior URL. Safe to run, but slow — takes ~15s per 20 articles.")) return;
              healImages(true);
            }}
            disabled={healingImages}
            variant="outline"
            className="border-amber-400 text-amber-700 hover:bg-amber-50"
            data-testid="news-force-remirror-btn"
          >
            {healingImages ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Force re-mirror ALL
          </Button>
          <Button
            onClick={runIngest}
            disabled={ingesting}
            className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90"
            data-testid="news-refresh-feeds"
          >
            {ingesting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh feeds now
          </Button>
        </div>
      </div>

      <Tabs value={status} onValueChange={setStatus} className="w-full">
        <div className="px-6 pt-4">
          <TabsList>
            <TabsTrigger value="pending" data-testid="news-tab-pending">
              Pending
            </TabsTrigger>
            <TabsTrigger value="published" data-testid="news-tab-published">
              Published
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="news-tab-rejected">
              Rejected
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={status} className="p-6">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-gray-500 font-body">
              {status === "pending"
                ? "The queue is empty. Click Refresh feeds now to pull the latest."
                : `No ${status} articles yet.`}
            </div>
          ) : (
            <div className="space-y-4">
              {rows.map((a) => (
                <NewsRow
                  key={a.article_id}
                  article={a}
                  status={status}
                  busy={busyId === a.article_id}
                  onApprove={() => approve(a.article_id)}
                  onReject={() => reject(a.article_id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function NewsRow({ article: a, status, busy, onApprove, onReject }) {
  const [debugOpen, setDebugOpen] = useState(false);
  const [debug, setDebug] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [reheal, setReheal] = useState(false);

  const loadDebug = async () => {
    setDebugLoading(true);
    setDebugOpen(true);
    try {
      const r = await axios.get(`${API}/admin/news/image-debug/${a.article_id}`, {
        withCredentials: true,
      });
      setDebug(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load debug info");
      setDebugOpen(false);
    } finally {
      setDebugLoading(false);
    }
  };

  const runReheal = async () => {
    setReheal(true);
    try {
      const r = await axios.post(
        `${API}/admin/news/reheal/${a.article_id}`,
        {},
        { withCredentials: true },
      );
      if (r.data.ok) {
        toast.success(`Re-healed via ${r.data.source}`);
      } else {
        toast.error(`Couldn't heal — ${r.data.reason || "unknown"}`);
      }
      await loadDebug();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-heal failed");
    } finally {
      setReheal(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-3 p-4 border border-gray-100 rounded-xl hover:bg-tbc-cream/30 transition-colors"
      data-testid={`news-row-${a.article_id}`}
    >
      <div className="flex flex-col md:flex-row gap-4">
        {a.image_url ? (
          <img
            src={a.image_url}
            alt={a.title}
            className="w-full md:w-40 h-24 rounded-lg object-cover bg-gray-100 flex-shrink-0"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-full md:w-40 h-24 rounded-lg bg-tbc-cream flex items-center justify-center flex-shrink-0">
            <Newspaper className="w-8 h-8 text-tbc-gold/60" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-widest text-tbc-gold border border-tbc-gold/40 px-2 py-0.5 rounded-full font-body">
              {a.source_name || "Manual"}
            </span>
            {a.category && (
              <span className="text-xs text-gray-500 font-body">{a.category}</span>
            )}
            {a.published_at && (
              <span className="text-xs text-gray-400 font-body flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(a.published_at).toLocaleDateString()}
              </span>
            )}
            {a.image_mirrored ? (
              <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold" title={`Source: ${a.image_source_type || "?"}`}>
                mirrored
              </span>
            ) : a.image_url ? (
              <span className="text-[10px] uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-semibold">
                hotlink
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full font-semibold">
                no image
              </span>
            )}
          </div>
          <h3 className="font-heading text-tbc-navy text-base leading-snug">
            {a.title}
          </h3>
          {a.excerpt && (
            <p className="text-sm text-gray-600 font-body mt-1 line-clamp-2 leading-relaxed">
              {a.excerpt}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-2">
            {a.source_url && (
              <a
                href={a.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-tbc-gold font-body hover:underline"
              >
                View original <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <button
              type="button"
              onClick={() => (debugOpen ? setDebugOpen(false) : loadDebug())}
              className="inline-flex items-center gap-1 text-xs text-tbc-navy/70 hover:text-tbc-navy font-body hover:underline"
              data-testid={`news-debug-${a.article_id}`}
            >
              <Bug className="w-3 h-3" />
              {debugOpen ? "Hide image debug" : "Image debug"}
            </button>
          </div>
        </div>

        <div className="flex md:flex-col gap-2 md:justify-center">
          {status !== "published" && (
            <Button
              size="sm"
              disabled={busy}
              onClick={onApprove}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`news-approve-${a.article_id}`}
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                  Approve
                </>
              )}
            </Button>
          )}
          {status !== "rejected" && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={onReject}
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid={`news-reject-${a.article_id}`}
            >
              <XCircle className="w-3.5 h-3.5 mr-1" />
              Reject
            </Button>
          )}
        </div>
      </div>

      {debugOpen && (
        <div className="rounded-lg bg-tbc-navy/[0.03] border border-tbc-navy/10 p-3 space-y-2" data-testid={`news-debug-panel-${a.article_id}`}>
          {debugLoading || !debug ? (
            <div className="flex items-center gap-2 text-xs text-tbc-navy/50">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching diagnostic…
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-2 text-[11px] font-mono text-tbc-navy/80">
                <DebugField label="Source URL" value={debug.source_url} isLink />
                <DebugField label="Detected og:image" value={debug.live_resolution?.url} isLink />
                <DebugField label="Detection source" value={debug.live_resolution?.source} />
                <DebugField label="Article page HTTP" value={debug.live_resolution?.http_status} />
                <DebugField label="Saved image URL" value={debug.stored?.image_url} isLink />
                <DebugField label="Original publisher URL" value={debug.stored?.image_original_url} isLink />
                <DebugField label="Mirrored?" value={debug.stored?.image_mirrored ? `yes (${debug.stored?.image_source_type || "?"})` : "no"} />
                <DebugField
                  label="Live HEAD status"
                  value={
                    debug.live_head?.status !== null && debug.live_head?.status !== undefined
                      ? `${debug.live_head.status} (${debug.live_head.ok ? "OK" : "broken"})`
                      : debug.live_head?.error || "—"
                  }
                />
                <DebugField label="Fallback reason" value={debug.stored?.image_rejection_reason || debug.live_resolution?.rejection_reason} />
              </div>
              <div>
                <Button
                  size="sm"
                  onClick={runReheal}
                  disabled={reheal}
                  className="bg-tbc-navy text-white hover:bg-tbc-navy/90 text-xs h-8"
                  data-testid={`news-reheal-${a.article_id}`}
                >
                  {reheal ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  Re-heal this article
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DebugField({ label, value, isLink }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] uppercase tracking-widest text-tbc-navy/40">{label}</span>
      {empty ? (
        <span className="text-tbc-navy/30 italic">—</span>
      ) : isLink && typeof value === "string" && value.startsWith("http") ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-tbc-gold hover:underline truncate" title={value}>
          {value.length > 80 ? value.slice(0, 80) + "…" : value}
        </a>
      ) : (
        <span className="truncate" title={String(value)}>{String(value)}</span>
      )}
    </div>
  );
}
